import { useState, useEffect } from 'react';
import { Database, MessageSquare, Building, Users, Bell, Loader, Plus, Search, Edit, Trash2, CheckCircle, Tag, Paperclip, UserPlus, BarChart } from 'lucide-react';
import annotationsService from '../services/annotations';
import { searchOrganization, createOrganization, getOrganization } from '../services/organizations';

export default function Metier() {
  const [activeTab, setActiveTab] = useState('annotations');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  // États pour les annotations
  const [newAnnotation, setNewAnnotation] = useState({
    content: '',
    targetType: 'project',
    targetId: '',
    priority: 'medium',
    tags: []
  });

  // États pour les organisations
  const [searchSiret, setSearchSiret] = useState('');
  const [orgName, setOrgName] = useState('');
  const [orgData, setOrgData] = useState({
    siret: '',
    name: '',
    address: '',
    city: '',
    postalCode: '',
    website: '',
    phone: ''
  });

  const tabs = [
    { id: 'annotations', label: 'Annotations Collaboratives', icon: MessageSquare },
    { id: 'organizations', label: 'Gestion Organisations', icon: Building },
    { id: 'stats', label: 'Statistiques', icon: BarChart }
  ];

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      if (activeTab === 'annotations') {
        try {
          const annotations = await annotationsService.getProjectAnnotations('demo-project-id');
          setData({ annotations: annotations || [] });
        } catch (err) {
          // Si erreur, utiliser des données vides au lieu de mock data
          console.warn('Impossible de charger les annotations, affichage vide:', err);
          setData({ annotations: [] });
        }
      } else if (activeTab === 'stats') {
        // Mock data pour les statistiques
        setData({
          stats: {
            totalAnnotations: 142,
            resolvedAnnotations: 98,
            totalOrganizations: 34,
            activeUsers: 156,
            mentionsToday: 23
          }
        });
      }
    } catch (err) {
      console.error('Erreur chargement données:', err);
      setError('Erreur lors du chargement des données');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAnnotation = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await annotationsService.createAnnotation({
        ...newAnnotation,
        targetId: newAnnotation.targetId || 'demo-target-123'
      });
      setNewAnnotation({
        content: '',
        targetType: 'project',
        targetId: '',
        priority: 'medium',
        tags: []
      });
      loadData();
    } catch (err) {
      setError('Erreur lors de la création de l\'annotation');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAnnotation = async (id) => {
    if (!window.confirm('Supprimer cette annotation ?')) return;
    setLoading(true);
    try {
      await annotationsService.deleteAnnotation(id);
      loadData();
    } catch (err) {
      setError('Erreur lors de la suppression');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchOrganization = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const result = await searchOrganization({ siret: searchSiret, name: orgName });
      if (result.success) {
        setData({ searchResults: result.data });
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Erreur lors de la recherche');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrganization = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const result = await createOrganization(orgData);
      if (result.success) {
        alert('Organisation créée avec succès !');
        setOrgData({
          siret: '',
          name: '',
          address: '',
          city: '',
          postalCode: '',
          website: '',
          phone: ''
        });
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader className="w-8 h-8 animate-spin text-green-600" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      );
    }

    switch (activeTab) {
      case 'annotations':
        return (
          <div className="space-y-6">
            {/* Formulaire création annotation */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Plus className="w-5 h-5 text-green-600" />
                Créer une annotation
              </h3>
              <form onSubmit={handleCreateAnnotation} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contenu (utilisez @username pour mentionner)
                  </label>
                  <textarea
                    value={newAnnotation.content}
                    onChange={(e) => setNewAnnotation({ ...newAnnotation, content: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    rows="3"
                    placeholder="Écrivez votre annotation... Utilisez @nom pour mentionner quelqu'un"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Type de cible</label>
                    <select
                      value={newAnnotation.targetType}
                      onChange={(e) => setNewAnnotation({ ...newAnnotation, targetType: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    >
                      <option value="project">Projet</option>
                      <option value="competence">Compétence</option>
                      <option value="certification">Certification</option>
                      <option value="entity">Entité</option>
                      <option value="correspondence">Correspondance</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Priorité</label>
                    <select
                      value={newAnnotation.priority}
                      onChange={(e) => setNewAnnotation({ ...newAnnotation, priority: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    >
                      <option value="low">Basse</option>
                      <option value="medium">Moyenne</option>
                      <option value="high">Haute</option>
                    </select>
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-3 rounded-lg font-medium hover:from-green-600 hover:to-emerald-700 transition-all flex items-center justify-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Créer l'annotation
                </button>
              </form>
            </div>

            {/* Liste des annotations */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Annotations récentes</h3>
              {data?.annotations && data.annotations.length > 0 ? (
                <div className="space-y-3">
                  {data.annotations.map((annotation) => (
                    <div key={annotation.id} className="bg-white rounded-lg p-5 border border-gray-200 hover:shadow-sm transition-shadow">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-start gap-3 flex-1">
                          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <MessageSquare className="w-5 h-5 text-green-600" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-gray-900">
                                {annotation.user?.firstName} {annotation.user?.lastName}
                              </span>
                              <span className={`px-2 py-0.5 text-xs rounded-full ${
                                annotation.priority === 'high' ? 'bg-red-100 text-red-700' :
                                annotation.priority === 'medium' ? 'bg-orange-100 text-orange-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {annotation.priority}
                              </span>
                              {annotation.isResolved && (
                                <CheckCircle className="w-4 h-4 text-green-600" />
                              )}
                            </div>
                            <p className="text-gray-700 text-sm mb-2">{annotation.content}</p>
                            <div className="flex items-center gap-3 text-xs text-gray-500">
                              <span>{new Date(annotation.createdAt).toLocaleDateString('fr-FR')}</span>
                              <span>•</span>
                              <span>{annotation.targetType}</span>
                              {annotation.mentionedUsers && annotation.mentionedUsers.length > 0 && (
                                <>
                                  <span>•</span>
                                  <span className="flex items-center gap-1">
                                    <UserPlus className="w-3 h-3" />
                                    {annotation.mentionedUsers.length} mention(s)
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDeleteAnnotation(annotation.id)}
                          className="text-red-600 hover:text-red-700 p-2 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      {annotation.tags && annotation.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {annotation.tags.map((tag, i) => (
                            <span key={i} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full flex items-center gap-1">
                              <Tag className="w-3 h-3" />
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
                  <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">Aucune annotation pour le moment</p>
                  <p className="text-sm text-gray-500 mt-1">Créez votre première annotation ci-dessus</p>
                </div>
              )}
            </div>
          </div>
        );

      case 'organizations':
        return (
          <div className="space-y-6">
            {/* Recherche organisation */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Search className="w-5 h-5 text-blue-600" />
                Rechercher une organisation
              </h3>
              <form onSubmit={handleSearchOrganization} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">SIRET</label>
                    <input
                      type="text"
                      value={searchSiret}
                      onChange={(e) => setSearchSiret(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="14 chiffres"
                      pattern="[0-9]{14}"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nom</label>
                    <input
                      type="text"
                      value={orgName}
                      onChange={(e) => setOrgName(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Nom de l'organisation"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-6 py-3 rounded-lg font-medium hover:from-blue-600 hover:to-indigo-700 transition-all flex items-center justify-center gap-2"
                >
                  <Search className="w-5 h-5" />
                  Rechercher
                </button>
              </form>

              {data?.searchResults && (
                <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-600">Résultats: {JSON.stringify(data.searchResults)}</p>
                </div>
              )}
            </div>

            {/* Création organisation */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Plus className="w-5 h-5 text-green-600" />
                Créer une organisation
              </h3>
              <form onSubmit={handleCreateOrganization} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">SIRET *</label>
                    <input
                      type="text"
                      value={orgData.siret}
                      onChange={(e) => setOrgData({ ...orgData, siret: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      placeholder="14 chiffres"
                      pattern="[0-9]{14}"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nom *</label>
                    <input
                      type="text"
                      value={orgData.name}
                      onChange={(e) => setOrgData({ ...orgData, name: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      placeholder="Nom de l'organisation"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Adresse</label>
                  <input
                    type="text"
                    value={orgData.address}
                    onChange={(e) => setOrgData({ ...orgData, address: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="Adresse complète"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Ville</label>
                    <input
                      type="text"
                      value={orgData.city}
                      onChange={(e) => setOrgData({ ...orgData, city: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      placeholder="Ville"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Code postal</label>
                    <input
                      type="text"
                      value={orgData.postalCode}
                      onChange={(e) => setOrgData({ ...orgData, postalCode: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      placeholder="75001"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Site web</label>
                    <input
                      type="url"
                      value={orgData.website}
                      onChange={(e) => setOrgData({ ...orgData, website: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      placeholder="https://exemple.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone</label>
                    <input
                      type="tel"
                      value={orgData.phone}
                      onChange={(e) => setOrgData({ ...orgData, phone: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      placeholder="01 23 45 67 89"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-3 rounded-lg font-medium hover:from-green-600 hover:to-emerald-700 transition-all flex items-center justify-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Créer l'organisation
                </button>
              </form>
            </div>
          </div>
        );

      case 'stats':
        return (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="text-3xl font-bold text-gray-900">{data?.stats?.totalAnnotations || 0}</div>
                  <div className="text-sm text-gray-600">Annotations totales</div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="text-3xl font-bold text-gray-900">{data?.stats?.resolvedAnnotations || 0}</div>
                  <div className="text-sm text-gray-600">Annotations résolues</div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center">
                  <Building className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="text-3xl font-bold text-gray-900">{data?.stats?.totalOrganizations || 0}</div>
                  <div className="text-sm text-gray-600">Organisations</div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-6 border border-orange-200">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="text-3xl font-bold text-gray-900">{data?.stats?.activeUsers || 0}</div>
                  <div className="text-sm text-gray-600">Utilisateurs actifs</div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-xl p-6 border border-yellow-200">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-yellow-500 rounded-lg flex items-center justify-center">
                  <Bell className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="text-3xl font-bold text-gray-900">{data?.stats?.mentionsToday || 0}</div>
                  <div className="text-sm text-gray-600">Mentions aujourd'hui</div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white shadow-lg">
              <Database className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-3xl font-heading font-bold text-gray-900">Métier</h1>
              <p className="text-gray-600">Plateforme de base - Cœur technique et administratif</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-2 mb-6">
          <div className="flex flex-wrap gap-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-md'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
